import { from, map, Observable, pluck, of } from 'rxjs';
import { HttpService } from '@/app/Data/services/Networking/HttpService';
import { environment } from '@/environments/environment';
import { HttpRequestMethod } from '@/app/Data/services/Networking/HttpRequestMethod';
import { SuccessApiResponse } from '@/app/Data/services/Networking/ApiResponse';
import { Injectable } from '@angular/core';
import { ITasksRepository } from '@/app/core/IRepositories/ITasksRepository';
import { Task, TaskFilter, TaskStatus } from '@/app/core/Models/Task';
import { DatabaseService } from '@/app/Data/services/Database/database.service';
import { liveQuery } from 'dexie';
import { TaskFilterDto } from '../DTO/TasksListDto';
import dayjs from "dayjs";
import { tap } from 'rxjs/operators';

// Cache interface for type safety
interface TasksCacheEntry {
  data: { results: Task[], total: number, status_count: { open: number, 'in-progress': number, done: number, trash: number } };
  timestamp: number;
  filter: TaskFilterDto;
}

@Injectable()
export class TasksRepository extends ITasksRepository {
  // Smart cache with TTL
  private tasksCache = new Map<string, TasksCacheEntry>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes TTL
  private readonly DEBUG_CACHE = false; // Set to true for cache debugging

  constructor(
    private httpService: HttpService,
    private databaseService: DatabaseService
  ) {
    super();
  }

  getTasks(filter: TaskFilter, status: TaskStatus): Observable<Task[]> {
    const requestURL = `${environment.apiURL}tasks-v3/${filter}/status/${status}`;
    const options = this.httpService.createOptions(
      HttpRequestMethod.get,
      this.httpService.createHeader(),
      requestURL,
      null,
      false
    );
    return this.httpService.execute(options).pipe(
      pluck('results'),
      map((item) => {
        let res = item as SuccessApiResponse<Task[]>;
        return res.results;
      })
    );
  }

  /**
   * Enhanced getTasksV2 with smart caching
   * @param filter - Task filter criteria
   * @param forceRefresh - Skip cache and fetch fresh data
   * @param sourceComponent - Component requesting data (for debugging)
   * @returns Observable with cached or fresh task data
   */
  getTasksV2(
    filter: TaskFilterDto, 
    forceRefresh: boolean = false,
    sourceComponent?: 'home' | 'tasks'
  ): Observable<{ results: Task[], total: number, status_count: { open: number, 'in-progress': number, done: number, trash: number } }> {
    const cacheKey = this.generateCacheKey(filter);
    const cached = this.tasksCache.get(cacheKey);
    
    // Debug logging
    if (this.DEBUG_CACHE) {
      console.log(`📦 Cache request from ${sourceComponent || 'unknown'}:`, {
        cacheKey,
        forceRefresh,
        hasCached: !!cached,
        cacheValid: cached ? this.isCacheValid(cached.timestamp) : false
      });
    }
    
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && cached && this.isCacheValid(cached.timestamp)) {
      if (this.DEBUG_CACHE) {
        console.log('✅ Returning cached data for', sourceComponent);
      }
      return of(cached.data);
    }
    
    // Fetch fresh data from API
    if (this.DEBUG_CACHE) {
      console.log('🌐 Fetching fresh data from API for', sourceComponent);
    }
    
    const requestURL = `${environment.apiURL}tasks-v3/getAll`;
    const options = this.httpService.createOptions(
      HttpRequestMethod.post,
      this.httpService.createHeader(),
      requestURL,
      filter,
      false
    );
    
    return this.httpService.execute(options).pipe(
      pluck('results'),
      map((item) => {
        let res = item as { results: Task[], total: number, status_count: { open: number, 'in-progress': number, done: number, trash: number } };
        return res;
      }),
      tap((data) => {
        // Cache the fresh data
        this.setCacheEntry(cacheKey, data, filter);
        if (this.DEBUG_CACHE) {
          console.log('💾 Cached fresh data:', { cacheKey, taskCount: data.results.length });
        }
      })
    );
  }

  /**
   * Generate cache key based on filter criteria
   */
  private generateCacheKey(filter: TaskFilterDto): string {
    const normalizedFilter = {
      assignees: filter.assignees?.sort() || [],
      status: filter.status?.sort() || [],
      priorities: filter.priorities?.sort() || [],
      tags: filter.tags?.sort() || [],
      due_date: filter.due_date || null,
      title: filter.title || null,
      page: filter.page || 1,
      page_size: filter.page_size || 50
    };

    return `tasks_${JSON.stringify(normalizedFilter)}`;
  }

  /**
   * Check if cache entry is still valid based on TTL
   */
  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_TTL;
  }

  /**
   * Store data in cache
   */
  private setCacheEntry(
    cacheKey: string, 
    data: { results: Task[], total: number, status_count: { open: number, 'in-progress': number, done: number, trash: number } }, 
    filter: TaskFilterDto
  ): void {
    this.tasksCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      filter
    });
  }

  /**
   * Invalidate specific cache entry
   */
  invalidateCacheEntry(filter: TaskFilterDto): void {
    const cacheKey = this.generateCacheKey(filter);
    this.tasksCache.delete(cacheKey);
    if (this.DEBUG_CACHE) {
      console.log('🗑️ Invalidated cache entry:', cacheKey);
    }
  }

  /**
   * Invalidate all cache entries
   */
  invalidateAllCache(): void {
    const size = this.tasksCache.size;
    this.tasksCache.clear();
    if (this.DEBUG_CACHE) {
      console.log(`🗑️ Cleared all cache entries: ${size} entries removed`);
    }
  }

  /**
   * Invalidate cache entries related to specific task statuses
   */
  invalidateCacheByStatus(statuses: TaskStatus[]): void {
    const toDelete: string[] = [];
    
    this.tasksCache.forEach((entry, key) => {
      // If cache entry includes any of the affected statuses, invalidate it
      if (entry.filter.status?.some(status => statuses.includes(status as TaskStatus))) {
        toDelete.push(key);
      }
    });
    
    toDelete.forEach(key => this.tasksCache.delete(key));
    
    if (this.DEBUG_CACHE && toDelete.length > 0) {
      console.log('🗑️ Invalidated cache entries by status:', { statuses, deletedKeys: toDelete });
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { entries: number, oldestEntry: Date | null, newestEntry: Date | null } {
    if (this.tasksCache.size === 0) {
      return { entries: 0, oldestEntry: null, newestEntry: null };
    }
    
    let oldest = Date.now();
    let newest = 0;
    
    this.tasksCache.forEach(entry => {
      oldest = Math.min(oldest, entry.timestamp);
      newest = Math.max(newest, entry.timestamp);
    });
    
    return {
      entries: this.tasksCache.size,
      oldestEntry: new Date(oldest),
      newestEntry: new Date(newest)
    };
  }

  saveTask(request: Task): void {
    this.databaseService.tasksList.put(request);
  }

  saveTasks(tasks: Task[]): void {
    this.databaseService
      .transaction('rw', [this.databaseService.tasksList], async () => {
        const localTasks = await this.databaseService.tasksList.toArray();
        const localTasksMap = new Map(localTasks.map(t => [t.task_id, t]));
        const newTasksKeys = tasks.map((item) => item.task_id);
        const deletedTasks = localTasks.filter(({ task_id }) => newTasksKeys.indexOf(task_id) == -1)
        if (deletedTasks.length) {
          await this.databaseService.tasksList.bulkDelete(deletedTasks.map((item) => item.task_id as unknown as string));
        }
        
        // Smart merge: preserve local task status if it was updated recently (within 10 seconds)
        // This prevents API responses from overwriting manual status updates before they're persisted
        const tasksToSave = tasks.map(apiTask => {
          const localTask = localTasksMap.get(apiTask.task_id);
          if (localTask) {
            const localUpdated = new Date(localTask.updated_dt || 0).getTime();
            const apiUpdated = new Date(apiTask.updated_dt || 0).getTime();
            const tenSecondsAgo = Date.now() - 10000;
            
            // If local update is recent and newer than API, preserve local status
            if (localUpdated > tenSecondsAgo && localUpdated > apiUpdated) {
              return {
                ...apiTask,
                status: localTask.status,
                updated_dt: localTask.updated_dt
              };
            }
          }
          return apiTask;
        });
        
        await this.databaseService.tasksList.bulkPut(tasksToSave);
      })
      .catch((error) => {
        console.error('Transaction Failed: ', error);
      });
  }

  appendTasks(tasks: Task[]): void {
    this.databaseService.tasksList.bulkPut(tasks);
  }

  getLocalTasks(filter: TaskFilter): Observable<Task[]> {
    return from(
      liveQuery(() => {
        this.databaseService.tasksList.mapToClass(Task);
        return this.databaseService.tasksList
          .filter((item) => filter == TaskFilter.Mine ? item.filter == filter : true)
          .sortBy('created_dt')
      })
    );
  }

  getLocalTasksV2(filter?: TaskFilterDto): Observable<Task[]> {
    return from(
      liveQuery(() => {
        return this.databaseService.tasksList.toArray();
        // .filter((item: any) => {
        //   // if (filter !== undefined && Object.keys(filter).length !== 0) {
        //   //   return this.filterTasks(item, filter);
        //   // } else {
        //   //   return item;
        //   // }
        //   return item;
        // }).toArray();
      })
    );
  }

  filterTasks(task: Task, filter: TaskFilterDto): any {
    let match = true;
    if (filter.assignees && filter.assignees.length > 0) {
      const taskAssigneeIds = task.task_assignees?.map(assignee => assignee.user_id) ?? [];
      if (!filter.assignees.some(assigneeId => taskAssigneeIds.includes(assigneeId))) {
        match = false;
      }
    }
    if (filter.priorities && filter.priorities.length > 0) {
      const capitalizedPriorities = filter.priorities.map(p => p.charAt(0).toUpperCase() + p.slice(1));
      if (!capitalizedPriorities.includes(task.task_priority ?? '')) {
        match = false;
      }
    }
    if (filter.status && filter.status.length > 0) {
      if (!filter.status.includes(task.status)) {
        match = false;
      }
    }
    if (filter.tags && filter.tags.length > 0) {
      const tagIds = task.tags?.map(tag => tag.tag_id) ?? [];
      if (!filter.tags.some(tagId => tagIds.includes(tagId))) {
        match = false;
      }
    }
    if (filter.due_date) {
      if (task.deadline_raw !== filter.due_date) {
        match = false;
      }
    }
    if (match) {
      return task;
    }
  }



  getTasksByChannelId(
    filter: TaskFilter,
    status: TaskStatus,
    channelId: number
  ): Observable<Task[]> {
    const requestURL = `${environment.apiURL}tasks/${filter}/status/${status}/channels/${channelId}`;
    const options = this.httpService.createOptions(
      HttpRequestMethod.get,
      this.httpService.createHeader(),
      requestURL,
      null,
      false
    );
    return this.httpService.execute(options).pipe(
      pluck('results'),
      map((item) => {
        let res = item as SuccessApiResponse<Task[]>;
        return res.results;
      })
    );
  }

  getLocalTaskById(taskId: number): Observable<Task | undefined> {
    return from(
      liveQuery(() => {
        this.databaseService.tasks.mapToClass(Task);
        return this.databaseService.tasksList.get(taskId as unknown as string);
      })
    );
  }

}
